import React, { useState } from "react";
import { Mail, MessageSquare, Building2, Globe2, Shield, ArrowRight } from "lucide-react";

const CONTACTS = [
  { Icon: MessageSquare, label: "GENERAL SUPPORT", email: "support@cloudrentvps.com", desc: "Account, login, server access questions." },
  { Icon: Building2, label: "SALES & ENTERPRISE", email: "sales@cloudrentvps.com", desc: "Custom plans, volume pricing, SLAs." },
  { Icon: Shield, label: "BILLING & REFUNDS", email: "billing@cloudrentvps.com", desc: "Invoices, refunds, payment disputes." },
  { Icon: Globe2, label: "LEGAL & PRIVACY", email: "legal@cloudrentvps.com", desc: "Legal notices, GDPR, DMCA, abuse reports." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "support", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const to =
      form.topic === "sales" ? "sales@cloudrentvps.com"
      : form.topic === "billing" ? "billing@cloudrentvps.com"
      : form.topic === "legal" ? "legal@cloudrentvps.com"
      : "support@cloudrentvps.com";
    const subject = encodeURIComponent(`[CloudRent · ${form.topic.toUpperCase()}] ${form.name || "Inquiry"}`);
    const body = encodeURIComponent(`From: ${form.name} <${form.email}>\n\n${form.message}`);
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <div data-testid="contact-page" className="container-pad py-12 md:py-20">
      <div className="overline">// CONTACT</div>
      <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tight max-w-3xl">
        Talk to a <span className="text-signal">human.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-zinc-400 text-base md:text-lg">
        We answer email within one business day. For urgent issues on a paid server,
        include your order ID and we will prioritize the response.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CONTACTS.map(({ Icon, label, email, desc }) => (
            <a
              key={email}
              href={`mailto:${email}`}
              data-testid={`contact-${email.split("@")[0]}`}
              className="card-flat p-5 hover:border-zinc-600 transition-colors block"
            >
              <div className="flex items-start gap-3">
                <div className="inline-flex h-9 w-9 items-center justify-center bg-zinc-900 border border-zinc-800 rounded-sm">
                  <Icon size={16} className="text-signal" />
                </div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">{label}</div>
                  <div className="mt-1 font-mono text-sm text-white break-all">{email}</div>
                  <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            </a>
          ))}

          <div className="card-flat p-5 sm:col-span-2">
            <div className="overline mb-3">// REGISTERED BUSINESS</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 font-mono text-sm">
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-widest">LEGAL ENTITY</div>
                <div className="text-zinc-100">CloudRent Hosting Ltd.</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-widest">SUPPORT HOURS</div>
                <div className="text-zinc-100">Mon–Fri · 09:00–18:00 UTC</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-widest">RESPONSE SLA</div>
                <div className="text-zinc-100">&lt; 24 hours (business days)</div>
              </div>
              <div>
                <div className="text-zinc-500 text-[11px] uppercase tracking-widest">PRIMARY CONTACT</div>
                <div className="text-zinc-100">support@cloudrentvps.com</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-5 card-flat p-6" data-testid="contact-form">
          <div className="overline">// SEND MESSAGE</div>
          <div className="mt-4 space-y-4">
            <div>
              <label className="label-flat">NAME</label>
              <input data-testid="contact-name" required className="input-flat" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ada Lovelace" />
            </div>
            <div>
              <label className="label-flat">EMAIL</label>
              <input data-testid="contact-email" type="email" required className="input-flat" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div>
              <label className="label-flat">TOPIC</label>
              <select data-testid="contact-topic" className="input-flat" value={form.topic}
                      onChange={(e) => setForm({ ...form, topic: e.target.value })}>
                <option value="support">General support</option>
                <option value="sales">Sales / enterprise</option>
                <option value="billing">Billing / refunds</option>
                <option value="legal">Legal / privacy</option>
              </select>
            </div>
            <div>
              <label className="label-flat">MESSAGE</label>
              <textarea data-testid="contact-message" required rows={5} className="input-flat resize-none" value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us how we can help…" />
            </div>
          </div>
          <button data-testid="contact-submit" className="btn-signal w-full h-11 mt-6 text-sm font-mono tracking-widest">
            <Mail size={14} /> {sent ? "OPENING MAIL CLIENT…" : "SEND VIA EMAIL"} <ArrowRight size={14} />
          </button>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-center">
            Form opens your mail client · No data stored on this page
          </div>
        </form>
      </div>
    </div>
  );
}
